/**
 * AnswerEntryModal.tsx — Inline Answer Entry (expands inside QaPanel row)
 *
 * NEW component — Helix v-final (2026-05-08)
 *
 * NOT a floating modal. Renders inline within the right panel.
 * Expands the question row when user clicks "Answer" or clicks an answered row.
 *
 * Contains:
 *   1. 5-point graded scale: Strong / Mostly / Partial / Weak / Absent
 *   2. Evidence / Assumption toggle (default: Assumption)
 *   3. Optional notes textarea (280 char max)
 *   4. Save button → writes to account_answers, fires aip:answer-saved
 *   5. M-009: Cross-CP out-of-order capture prompts (onBlur of notes textarea)
 *
 * Brand:
 *   Black #0B0B0F, Red #DC2626, Soft white #FAFAF7
 *   No exclamation points, no emojis
 *   Inter body, DM Sans display
 */

import { useState, useMemo } from "react";
import { useUnloadWarning } from "@/lib/useUnloadWarning";
import { useQuestionCoaching } from "@/lib/question-coaching";
import type { QuestionCoaching } from "@/lib/question-coaching";
import {
  detectCrossCpMentions,
  type CrossCpMatch,
} from "@/lib/cross-cp-prompts";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AnswerEntry {
  question_key: string;
  answer_value: string | null;  // "Strong" | "Mostly" | "Partial" | "Weak" | "Absent" | null
  points: number | null;        // 4 | 3 | 2 | 1 | 0 | null
  is_evidence: boolean;         // true = evidence-backed, false = assumption
  answer_notes: string;         // up to 280 chars
  answered_at: string;          // ISO 8601
  answered_by: string;          // user identifier
}

interface GradedOption {
  label: string;
  anchor: string;
  value: string;  // "4" | "3" | "2" | "1" | "0"
  points: number;
  borderColor: string;
  bgColor: string;
}

const GRADED_OPTIONS: GradedOption[] = [
  { label: "Strong yes — confirmed / fully in place",  anchor: "Strong",  value: "4", points: 4, borderColor: "#16A34A", bgColor: "rgba(22,163,74,0.08)" },
  { label: "Mostly yes — likely / mostly in place",    anchor: "Mostly",  value: "3", points: 3, borderColor: "#16A34A", bgColor: "rgba(22,163,74,0.07)" },
  { label: "Partial — mixed signals / unclear",        anchor: "Partial", value: "2", points: 2, borderColor: "#EAB308", bgColor: "rgba(234,179,8,0.07)" },
  { label: "Weak — mostly missing / mostly no",        anchor: "Weak",    value: "1", points: 1, borderColor: "#EA580C", bgColor: "rgba(234,88,12,0.07)" },
  { label: "Absent — hard no / blocked",               anchor: "Absent",  value: "0", points: 0, borderColor: "#DC2626", bgColor: "rgba(220,38,38,0.07)" },
];

interface Props {
  questionKey: string;
  questionLabel: string;
  /** Existing answer to pre-populate (for editing) */
  existingAnswer?: {
    value: string | null;
    is_evidence: boolean;
    notes: string;
  };
  onSave: (entry: Omit<AnswerEntry, "answered_at" | "answered_by">) => void;
  onCancel: () => void;
  /** User identifier for audit trail */
  userId?: string;
  /** When true, all answer-entry controls are disabled (read-only view for managers/execs). */
  readOnly?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AnswerEntryModal({
  questionKey,
  questionLabel,
  existingAnswer,
  onSave,
  onCancel,
  userId = "user",
  readOnly = false,
}: Props) {
  const { coachingMap } = useQuestionCoaching();
  const coaching = coachingMap[questionKey] ?? null;
  const [selectedValue, setSelectedValue] = useState<string | null>(
    existingAnswer?.value ?? null
  );
  const [isEvidence, setIsEvidence] = useState<boolean>(
    existingAnswer?.is_evidence ?? false  // Default: Assumption
  );
  const [notes, setNotes] = useState<string>(existingAnswer?.notes ?? "");
  const [saving, setSaving] = useState(false);

  // M-009: out-of-order capture state
  // Extract current CP number from the question key (e.g. "cp5_budget_source" → 5)
  const currentCpNumber = useMemo(() => {
    const m = questionKey.match(/^cp(\d+)_/);
    return m ? parseInt(m[1], 10) : 0;
  }, [questionKey]);
  const [crossCpMatches, setCrossCpMatches] = useState<CrossCpMatch[]>([]);
  const [dismissedCps, setDismissedCps] = useState<Set<number>>(new Set());
  const [crossCpToast, setCrossCpToast] = useState<string | null>(null);

  function handleNotesCrossCpCheck() {
    if (!notes.trim()) {
      setCrossCpMatches([]);
      return;
    }
    const matches = detectCrossCpMentions(notes, currentCpNumber);
    // Filter out any CPs the rep already dismissed in this session
    setCrossCpMatches(matches.filter((m) => !dismissedCps.has(m.cp)));
  }

  function handleCrossCpDismiss(cp: number) {
    setDismissedCps((prev) => new Set([...prev, cp]));
    setCrossCpMatches((prev) => prev.filter((m) => m.cp !== cp));
  }

  function handleCrossCpLog(match: CrossCpMatch) {
    // v1: toast directing rep to navigate manually.
    // v1.1 will open a second AnswerEntryModal pre-targeted at the most
    // relevant question in that CP (requires shared modal-stack context).
    setCrossCpToast(
      `We will add that link in v1.1 — for now, navigate to CP${match.cp} (${match.cpLabel}) manually.`,
    );
    setTimeout(() => setCrossCpToast(null), 4000);
    handleCrossCpDismiss(match.cp);
  }

  const selectedOption = GRADED_OPTIONS.find((o) => o.value === selectedValue);

  // Warn if the user has selected a grade or typed notes that differ from
  // any pre-existing answer and tries to close the tab without clicking Save.
  const isDirty =
    !saving &&
    (selectedValue !== (existingAnswer?.value ?? null) ||
      notes !== (existingAnswer?.notes ?? "") ||
      isEvidence !== (existingAnswer?.is_evidence ?? false));
  useUnloadWarning(
    isDirty,
    "You have an answer in progress. Click Save before leaving."
  );

  function handleSave() {
    if (!selectedValue) return;
    setSaving(true);

    const entry: Omit<AnswerEntry, "answered_at" | "answered_by"> = {
      question_key: questionKey,
      answer_value: selectedOption?.anchor ?? null,
      points: selectedOption?.points ?? null,
      is_evidence: isEvidence,
      answer_notes: notes.slice(0, 280),
    };

    onSave(entry);
    setSaving(false);
  }

  return (
    <>
      <style>{`
        @keyframes aeModalExpand {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ae-modal {
          animation: aeModalExpand 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
          background: #FAFAF7;
          border: 1px solid #E5E5E7;
          border-radius: 10px;
          padding: 16px;
          margin-top: 10px;
        }
        .ae-modal__label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #6B6B70;
          margin-bottom: 10px;
        }
        .ae-option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 11px 14px 11px 0;
          border-radius: 8px;
          border: 1px solid #E5E5E7;
          border-left-width: 4px;
          background: #FFFFFF;
          cursor: pointer;
          width: 100%;
          text-align: left;
          transition: background 0.1s, border-color 0.1s;
          margin-bottom: 6px;
          padding-left: 12px;
        }
        .ae-option:hover { background: #FAFAF7; }
        .ae-option--selected {
          border-color: #DC2626;
          background: #FEF2F2;
        }
        .ae-option__text {
          font-size: 14px;
          font-weight: 500;
          color: #0B0B0F;
          line-height: 1.4;
        }
        .ae-option__anchor {
          font-size: 12px;
          font-weight: 800;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ae-option__pts {
          font-size: 11px;
          font-weight: 700;
          color: #8E8E94;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ae-evidence-toggle {
          display: flex;
          gap: 0;
          border: 1px solid #E5E5E7;
          border-radius: 8px;
          overflow: hidden;
          margin-top: 14px;
        }
        .ae-evidence-btn {
          flex: 1;
          padding: 9px 12px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          color: #525258;
          transition: background 0.12s, color 0.12s;
          text-align: center;
          line-height: 1.3;
        }
        .ae-evidence-btn--active-evidence {
          background: #16A34A;
          color: #FFFFFF;
        }
        .ae-evidence-btn--active-assumption {
          background: #EA580C;
          color: #FFFFFF;
        }
        .ae-evidence-desc {
          font-size: 11px;
          color: #6B6B70;
          margin-top: 6px;
          line-height: 1.5;
          font-style: italic;
        }
        .ae-notes {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #C9C9CC;
          border-radius: 6px;
          color: #0B0B0F;
          font-size: 14px;
          padding: 8px 11px;
          resize: vertical;
          outline: none;
          font-family: inherit;
          line-height: 1.5;
          box-sizing: border-box;
          margin-top: 12px;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .ae-notes:focus {
          border-color: #DC2626;
          box-shadow: 0 0 0 3px rgba(220,38,38,0.12);
        }
        .ae-actions {
          display: flex;
          gap: 8px;
          margin-top: 14px;
          justify-content: flex-end;
        }
        .ae-btn {
          padding: 9px 18px;
          border-radius: 7px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: opacity 0.12s;
        }
        .ae-btn:disabled { opacity: 0.5; cursor: default; }
        .ae-btn--save {
          background: #DC2626;
          color: #FFFFFF;
          border: none;
        }
        .ae-btn--save:hover:not(:disabled) { opacity: 0.88; }
        .ae-btn--cancel {
          background: #FFFFFF;
          color: #0B0B0F;
          border: 1px solid #C9C9CC;
        }
        .ae-btn--cancel:hover { background: #FAFAF7; }
      `}</style>

      <div className="ae-modal" role="region" aria-label="Answer entry">
        {/* Per-question coaching panel (DB-sourced, collapsed by default) */}
        <AeCoachingPanel coaching={coaching} />

        {/* Scale */}
        <div className="ae-modal__label">Rate this question</div>
        {GRADED_OPTIONS.map((opt) => {
          const isSel = selectedValue === opt.value;
          return (
            <button
              key={opt.value}
              className={`ae-option${isSel ? " ae-option--selected" : ""}`}
              style={{
                borderLeftColor: opt.borderColor,
                backgroundColor: isSel ? opt.bgColor : "#FFFFFF",
                cursor: readOnly ? "not-allowed" : "pointer",
                opacity: readOnly ? 0.5 : 1,
              }}
              onClick={readOnly ? undefined : () => setSelectedValue(isSel ? null : opt.value)}
              disabled={readOnly}
              aria-pressed={isSel}
            >
              <span className="ae-option__text" style={{ fontWeight: isSel ? 700 : 500 }}>
                {opt.label}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span
                  className="ae-option__anchor"
                  style={{ color: opt.borderColor }}
                >
                  {opt.anchor}
                </span>
                <span className="ae-option__pts">{opt.points} {opt.points === 1 ? "pt" : "pts"}</span>
              </div>
            </button>
          );
        })}

        {/* Evidence / Assumption toggle */}
        <div className="ae-modal__label" style={{ marginTop: 18 }}>
          Confidence source
        </div>
        <div
          className="ae-evidence-toggle"
          role="group"
          aria-label="Evidence or assumption"
          style={{ opacity: readOnly ? 0.5 : 1, pointerEvents: readOnly ? "none" : undefined }}
        >
          <button
            className={`ae-evidence-btn${isEvidence ? " ae-evidence-btn--active-evidence" : ""}`}
            onClick={readOnly ? undefined : () => setIsEvidence(true)}
            disabled={readOnly}
            aria-pressed={isEvidence}
          >
            Evidence
          </button>
          <button
            className={`ae-evidence-btn${!isEvidence ? " ae-evidence-btn--active-assumption" : ""}`}
            onClick={readOnly ? undefined : () => setIsEvidence(false)}
            disabled={readOnly}
            aria-pressed={!isEvidence}
          >
            Assumption
          </button>
        </div>
        <div className="ae-evidence-desc">
          {isEvidence
            ? "I have documentation, emails, or meeting notes to back this."
            : "I believe this is true but have not yet confirmed it."}
        </div>

        {/* Notes */}
        <textarea
          className="ae-notes"
          value={notes}
          onChange={readOnly ? undefined : (e) => setNotes(e.target.value.slice(0, 280))}
          onBlur={readOnly ? undefined : handleNotesCrossCpCheck}
          readOnly={readOnly}
          placeholder="Notes (optional) — paste a quote, date, or source"
          rows={2}
          maxLength={280}
          aria-label="Answer notes"
          style={readOnly ? { cursor: "not-allowed", opacity: 0.6, background: "#F5F5F4" } : undefined}
        />
        <div style={{ fontSize: 10, color: "#8E8E94", textAlign: "right", marginTop: 3 }}>
          {notes.length}/280
        </div>

        {/* M-009: Cross-CP capture prompts */}
        {crossCpMatches.length > 0 && (
          <div
            style={{
              marginTop: 10,
              borderRadius: 8,
              border: "1px solid #EA580C",
              background: "rgba(234,88,12,0.06)",
              padding: "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
            role="region"
            aria-label="Out-of-order capture prompts"
          >
            {crossCpMatches.map((match) => (
              <div
                key={match.cp}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontSize: 12, color: "#1F1F23", fontWeight: 500, flex: 1, minWidth: 0 }}>
                  You mentioned{" "}
                  <strong style={{ fontWeight: 700 }}>{match.keyword}</strong>
                  {" "}— want to log that against CP{match.cp} ({match.cpLabel}) too?
                </span>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => handleCrossCpLog(match)}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "1px solid #EA580C",
                      background: "#EA580C",
                      color: "#FFFFFF",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Log against CP{match.cp}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCrossCpDismiss(match.cp)}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "1px solid #C9C9CC",
                      background: "transparent",
                      color: "#6B6B70",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* M-009: Cross-CP toast */}
        {crossCpToast && (
          <div
            style={{
              marginTop: 8,
              padding: "8px 12px",
              borderRadius: 7,
              background: "#1C1C21",
              color: "#FAFAF7",
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.5,
            }}
            role="status"
          >
            {crossCpToast}
          </div>
        )}

        {/* Actions — Save hidden in read-only mode */}
        <div className="ae-actions">
          <button className="ae-btn ae-btn--cancel" onClick={onCancel} type="button">
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button
              className="ae-btn ae-btn--save"
              onClick={handleSave}
              disabled={!selectedValue || saving}
              type="button"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── AeCoachingPanel ──────────────────────────────────────────────────────────
// Inline coaching panel for AnswerEntryModal. Collapsed by default.
// Same visual language as QuestionCoachingPanel in QaPanel.tsx.

function AeCoachingPanel({ coaching }: { coaching: QuestionCoaching | null }) {
  const [expanded, setExpanded] = useState(false);

  if (!coaching) return null;

  const whyPreview = coaching.why_it_matters
    ? (coaching.why_it_matters.split(/\.\s+/)[0] ?? "") + "."
    : null;

  return (
    <div
      style={{
        marginBottom: 12,
        borderRadius: 8,
        border: "1px solid #E5E5E7",
        background: "#FAFAF7",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "9px 12px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#DC2626",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            Coaching
          </span>
          {!expanded && whyPreview && (
            <span
              style={{
                fontSize: 12,
                color: "#525258",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {whyPreview}
            </span>
          )}
          {!expanded && (
            <span
              style={{
                fontSize: 11,
                color: "#8E8E94",
                fontWeight: 500,
                flexShrink: 0,
                marginLeft: "auto",
              }}
            >
              More guidance
            </span>
          )}
        </div>
        <span style={{ color: "#6B6B70", fontSize: 12, flexShrink: 0 }}>
          {expanded ? "▲" : "▼"}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: "0 12px 12px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ borderTop: "1px solid #E5E5E7", marginBottom: 2 }} />

          {coaching.why_it_matters && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#DC2626", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>
                Why it matters
              </div>
              <p style={{ fontSize: 13, color: "#0B0B0F", fontWeight: 500, lineHeight: 1.6, margin: 0 }}>
                {coaching.why_it_matters}
              </p>
            </div>
          )}

          {coaching.best_practice_answers && coaching.best_practice_answers.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#DC2626", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>
                What good looks like
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 5 }}>
                {coaching.best_practice_answers.map((bpa, i) => (
                  <li key={i} style={{ fontSize: 13, color: "#0B0B0F", fontWeight: 500, lineHeight: 1.55 }}>
                    <span style={{ fontWeight: 700 }}>{bpa.option_label}.</span>{" "}{bpa.guidance}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {coaching.recommendation && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#DC2626", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>
                Recommended approach
              </div>
              <p style={{ fontSize: 13, color: "#0B0B0F", fontWeight: 500, lineHeight: 1.6, margin: 0 }}>
                {coaching.recommendation}
              </p>
            </div>
          )}

          {coaching.narrative && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#6B6B70", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>
                Narrative context
              </div>
              <p style={{ fontSize: 12, color: "#525258", fontWeight: 500, lineHeight: 1.65, margin: 0 }}>
                {coaching.narrative}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
